
import React, { createContext, useContext, useState, useCallback } from 'react';

export interface LoadingState {
  isLoading: boolean;
  message: string;
  type: 'audio' | 'action' | 'data' | 'general' | 'initialization' | 'page';
  progress?: number;
  phase?: string;
}

interface LoadingContextValue {
  loadingStates: Record<string, LoadingState>;
  setLoading: (key: string, loading: boolean, message?: string, type?: LoadingState['type'], progress?: number, phase?: string) => void;
  clearLoading: (key: string) => void;
  clearAllLoading: () => void;
  isAnyLoading: boolean;
  getLoadingMessage: (key: string) => string | null;
  isLoading: (key: string) => boolean;
  getLoadingProgress: (key: string) => number | null;
  getLoadingPhase: (key: string) => string | null;
}

const LoadingContext = createContext<LoadingContextValue | undefined>(undefined);

export const LoadingProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [loadingStates, setLoadingStates] = useState<Record<string, LoadingState>>({});

  const setLoading = useCallback((
    key: string, 
    loading: boolean, 
    message: string = 'Loading...', 
    type: LoadingState['type'] = 'general',
    progress?: number,
    phase?: string
  ) => {
    console.log('🔄 [LoadingContext] setLoading called:', { key, loading, message, type });
    setLoadingStates(prev => {
      if (loading) {
        const newState = { ...prev, [key]: { isLoading: true, message, type, progress, phase } };
        console.log('🔄 [LoadingContext] Adding loading state:', newState);
        return newState;
      } else {
        const newState = { ...prev };
        delete newState[key];
        console.log('🔄 [LoadingContext] Removing loading state:', newState);
        return newState;
      }
    });
  }, []);

  const clearLoading = useCallback((key: string) => {
    console.log('🔄 [LoadingContext] clearLoading called:', key);
    setLoadingStates(prev => {
      const newState = { ...prev };
      delete newState[key];
      console.log('🔄 [LoadingContext] After clearLoading:', newState);
      return newState;
    });
  }, []);

  const clearAllLoading = useCallback(() => {
    console.log('🔄 [LoadingContext] clearAllLoading called');
    setLoadingStates({});
  }, []);

  const isAnyLoading = Object.keys(loadingStates).length > 0;

  const getLoadingMessage = useCallback((key: string) => {
    return loadingStates[key]?.message || null;
  }, [loadingStates]);

  const isLoading = useCallback((key: string) => {
    return loadingStates[key]?.isLoading || false;
  }, [loadingStates]);

  const getLoadingProgress = useCallback((key: string) => {
    return loadingStates[key]?.progress || null;
  }, [loadingStates]);

  const getLoadingPhase = useCallback((key: string) => {
    return loadingStates[key]?.phase || null;
  }, [loadingStates]);

  return (
    <LoadingContext.Provider value={{
      loadingStates,
      setLoading,
      clearLoading,
      clearAllLoading,
      isAnyLoading,
      getLoadingMessage,
      isLoading,
      getLoadingProgress,
      getLoadingPhase
    }}>
      {children}
    </LoadingContext.Provider>
  );
};

export const useLoading = () => {
  const context = useContext(LoadingContext);
  if (context === undefined) {
    throw new Error('useLoading must be used within a LoadingProvider');
  }
  return context;
};
